import clsx from 'clsx';
import { type ReactNode, useEffect, useState } from 'react';

import { AudioPreview } from './audio-preview';
import { ImagePreview } from './image-preview';
import { DefaultFilePreview } from './default-file-preview';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Switch } from '@flintmint/web-shared/components/switch';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Collapse } from '@flintmint/web-shared/components/collapse';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import type { MenuData } from '@flintmint/web-shared/components/menu';

import { formatDate, formatDuration, getSourceTypeLabel } from '@/utils/helper-utils';
import { getImagePreviewStoragePath } from '@flintmint/core-shared/rxdb';
import { isAudioSourceType } from '@/utils/file-upload';
import { resolveSourcePreviewUrl } from '@/utils/source-preview';
import { forgetPreviewUrl } from '@/services/firebase-storage-service';

import { useContextItemGrid } from './context-item-grid-context';
import { getUserSourceErrorMessage } from './user-source-error-message';

import styles from './context-item.module.scss';

const CORRUPTED_AUDIO_ERROR = 'Audio file might be corrupted or unsupported';

export const ContextItem = ({
  name,
  meta,
  error,
  source,
  enabled,
  onDelete,
  updatedAt,
  onPreview,
  sourceType,
  syncStatus,
  expandable,
  deleteOption,
  uploadStatus,
  uploadProgress,
  attached = true,
  showMenu = true,
  onToggleEnabled,
  onDetachFromNote,
  openAudioInAModal,
  autoLoadAudio = false,
  playbackEnabled = true,
  highlightAttached,
  parsedContent = '',
  onToggleAttachment,
  appearance = 'default',
  onMakeGloballyAvailable,
  ...rest
}: ContextItemProps) => {
  const isGrid = useContextItemGrid();
  const sourceTypeLabel = getSourceTypeLabel(sourceType);
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [audioDuration, setAudioDuration] = useState(0);
  // Which recording was asked for (by Play, or on mount for autoLoadAudio) and how many times, so a
  // Play press after a failed load is a new request while a failure on its own never re-requests.
  const [audioRequest, setAudioRequest] = useState<{ source: string; attempt: number }>();

  const isProcessing = syncStatus === 'processing';
  const processingFailed = syncStatus === 'error';
  const showError = !!error || processingFailed;
  const errorMessage = getContextItemErrorMessage(syncStatus, error);
  const isUploading = uploadStatus === 'uploading';
  const canShowPreview = !isProcessing && !showError && !isUploading;

  const isImageSource = !!sourceType?.startsWith('image/');
  const isAudioSource = isAudioSourceType(sourceType);
  const isExternalSource = !!source && (source.startsWith('http://') || source.startsWith('https://'));

  const showImagePreview = isImageSource && canShowPreview && !isExternalSource;
  const showAudioPreview = isAudioSource && canShowPreview;
  const isAudioRequested = showAudioPreview && !!source && audioRequest?.source === source;
  const isAudioLoading = isAudioRequested && !previewUrl;

  useEffect(() => {
    if (!showImagePreview || !source) {
      // Reprocessing can generate a preview this session already found missing.
      if (isImageSource && isProcessing && source) forgetPreviewUrl(getImagePreviewStoragePath(source));
      return setPreviewUrl(undefined);
    }

    let isCancelled = false;

    // Prefer the generated preview, but fall back to the original: a source uploaded before
    // previews existed (or one whose generation failed) has no object at the preview path, and
    // without this the card would sit on its loading skeleton forever.
    resolveSourcePreviewUrl(getImagePreviewStoragePath(source))
      .catch(() => resolveSourcePreviewUrl(source))
      .then((url) => !isCancelled && setPreviewUrl(url))
      .catch(() => !isCancelled && setPreviewUrl(undefined));

    return () => {
      isCancelled = true;
    };
  }, [showImagePreview, source, isImageSource, isProcessing]);

  useEffect(() => {
    if (showAudioPreview) return;

    setAudioDuration(0);
    setAudioRequest(undefined);
    // Drop a recording already fetched for this item. Holding the URL across the hidden state
    // would have the next mount download it and start playing with no Play press behind it.
    if (isAudioSource) setPreviewUrl(undefined);
  }, [showAudioPreview, isAudioSource]);

  useEffect(() => {
    if (!autoLoadAudio || !showAudioPreview || !source) return;
    setAudioRequest((previous) => (previous?.source === source ? previous : { source, attempt: 1 }));
  }, [autoLoadAudio, showAudioPreview, source]);

  useEffect(() => {
    if (!isAudioRequested || !source) return;

    let isCancelled = false;

    resolveSourcePreviewUrl(source)
      .then((url) => !isCancelled && setPreviewUrl(url))
      .catch(() => {
        if (isCancelled) return;
        setPreviewUrl(undefined);
        // The request is spent: only the next Play press asks again.
        setAudioRequest(undefined);
      });

    return () => {
      isCancelled = true;
    };
  }, [isAudioRequested, source, audioRequest]);

  const requestAudio = () => {
    if (!source) return;
    setAudioRequest((previous) => ({ source, attempt: (previous?.attempt ?? 0) + 1 }));
  };

  // Previews are written once and originals never change, so a URL that resolved and then fails to
  // render is an undecodable file or a transient fault; re-resolving would hand back the same URL
  // (React bails on identical state and nothing reloads), so the broken image is hidden instead.
  const handleImagePreviewError = () => setPreviewUrl(undefined);

  const handleAudioLoadError = () => {
    setPreviewUrl(undefined);
    setAudioRequest(undefined);
  };

  const fallbackDeleteOption: MenuData = onDelete
    ? [
        {
          onClick: onDelete,
          icon: 'file-remove',
          name: 'Delete',
          danger: true,
        },
      ]
    : [];

  const handleToggleAttachment = onToggleAttachment || onDetachFromNote;
  const attachmentMenuItemName = attached ? 'Detach From Note' : 'Attach to Note';
  const attachmentMenuItemIcon = attached ? 'file-remove' : 'file-add';
  const menu: MenuData = [
    {
      icon: 'eye',
      name: 'View Item',
      onClick: onPreview,
      disabled: !!error,
    },
    ...(handleToggleAttachment
      ? ([
          {
            type: 'divider',
          },
          {
            onClick: handleToggleAttachment,
            icon: attachmentMenuItemIcon,
            name: attachmentMenuItemName,
            disabled: !!error,
          },
        ] as MenuData)
      : []),
    ...(onMakeGloballyAvailable
      ? ([
          {
            type: 'divider',
          },
          {
            onClick: onMakeGloballyAvailable,
            icon: 'globe-02',
            name: 'Make Globally Available',
            disabled: !!error,
          },
        ] as MenuData)
      : []),
    {
      type: 'divider',
    },
    ...(deleteOption?.length ? deleteOption : fallbackDeleteOption),
  ];
  const hasMenuActions = showMenu && menu.length > 0;
  const hasActions = !!onToggleEnabled || !!onToggleAttachment || hasMenuActions;

  const renderMeta = (
    <div className={styles.titleHolder}>
      <p className={clsx(styles.title, 'ellipsis-text')}>{name}</p>
      <div data-testid="context-item-updated-date" className={styles.date}>
        {updatedAt ? (
          formatDate(updatedAt)
        ) : showError ? (
          formatDate(new Date().toISOString())
        ) : (
          <Skeleton trueHeight={16} height={10} width={100} />
        )}
      </div>
    </div>
  );

  const renderActions = (
    <>
      {!!onToggleEnabled && <Switch checked={!!enabled} onChange={onToggleEnabled} />}
      {!!onToggleAttachment && (
        <button
          disabled={showError}
          onClick={onToggleAttachment}
          data-testid="context-item-attachment-toggle"
          className={clsx(styles.attachmentToggle, {
            [styles.attached]: attached,
          })}
        >
          <Icon type={attached ? 'file-remove' : 'file-add'} />
        </button>
      )}
      {hasMenuActions && (
        <Popover menu={menu} placement="bottom">
          <Icon type="more-horizontal" data-testid="context-item-menu" />
        </Popover>
      )}
    </>
  );

  const getAudioTimeLabel = () => {
    if (!showAudioPreview || !audioDuration) return null;
    return formatDuration(audioDuration);
  };

  return (
    <div
      className={clsx(styles.contextItem, styles[`a-${appearance}`], {
        [styles.grid]: isGrid,
        [styles.showImagePreview]: showImagePreview,
        [styles.expandable]: expandable,
        [styles.highlightAttached]: highlightAttached,
      })}
      {...rest}
    >
      {!showImagePreview && (
        <DefaultFilePreview
          meta={renderMeta}
          actions={renderActions}
          uploadStatus={uploadStatus}
          showAudioPreview={isAudioSource}
          sourceTypeLabel={sourceTypeLabel}
          audioTimeLabel={getAudioTimeLabel()}
          hasActions={hasActions}
        />
      )}
      {showImagePreview && (
        <ImagePreview
          name={name}
          meta={renderMeta}
          previewUrl={previewUrl}
          actions={renderActions}
          uploadStatus={uploadStatus}
          hasActions={hasActions}
          onLoadError={handleImagePreviewError}
        />
      )}
      {showAudioPreview && (
        <AudioPreview
          previewUrl={previewUrl}
          waveformSeed={source ?? name}
          canLoadAudio={!!source}
          isAudioLoading={isAudioLoading}
          parsedContent={parsedContent}
          onRequestPlayback={requestAudio}
          onLoadError={handleAudioLoadError}
          onDurationChange={setAudioDuration}
          onReadMore={openAudioInAModal}
          playbackEnabled={playbackEnabled}
        />
      )}
      <Collapse open={isProcessing || showError || isUploading || !!meta} unmountOnCollapse>
        {isProcessing && (
          <div className={clsx(styles.processing, styles[`a-${appearance}`], styles.contextItemStatus)}>
            <Loader size={13} variant="steps" color="warning" data-testid="processing-spinner" />
            <span>We are processing the content...</span>
          </div>
        )}

        {showError && (
          <div className={clsx(styles.error, styles[`a-${appearance}`], styles.contextItemStatus)}>
            <Icon type="cancel-circle-half-dot" data-testid="processing-failed-icon" />
            <span>{errorMessage}</span>
          </div>
        )}

        {isUploading && (
          <div className={clsx(styles.uploading, styles[`a-${appearance}`], styles.contextItemStatus)}>
            <div
              style={{
                gap: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                fontSize: 12,
              }}
            >
              <Loader size={13} variant="steps" color="dark" data-testid="uploading-spinner" />
              <span>Uploading...</span>
            </div>
            <div
              style={{
                width: '100%',
                padding: '4px 0',
              }}
            >
              <Loader type="progress" progress={uploadProgress ?? 0} color="success" data-testid="upload-progress" />
            </div>
          </div>
        )}
        {!(isProcessing || showError || isUploading) && !!meta && <div className={styles.metaHolder}>{meta}</div>}
      </Collapse>
    </div>
  );
};

export type ManagedSyncStatus = 'ready' | 'processing' | 'error';
type UploadStatus = 'uploading' | 'error';

export interface ContextItemProps {
  id: string;
  name: string;
  enabled?: boolean;
  attached?: boolean;
  meta?: ReactNode;
  updatedAt?: string;
  showMenu?: boolean;
  expandable?: boolean;
  error?: string | null;
  onDelete?: () => void;
  source?: string | null;
  onPreview?: () => void;
  uploadProgress?: number;
  deleteOption?: MenuData;
  sourceType?: string | null;
  uploadStatus?: UploadStatus;
  parsedContent?: string | null;
  onDetachFromNote?: () => void;
  onMakeGloballyAvailable?: () => void;
  openAudioInAModal?: () => void;
  autoLoadAudio?: boolean;
  playbackEnabled?: boolean;
  onToggleEnabled?: () => void;
  onToggleAttachment?: () => void;
  syncStatus?: ManagedSyncStatus;
  highlightAttached?: boolean;
  appearance?: 'default' | 'compact';
}

const getContextItemErrorMessage = (syncStatus?: ManagedSyncStatus, error?: string | null): string => {
  if (error?.includes(CORRUPTED_AUDIO_ERROR)) {
    return CORRUPTED_AUDIO_ERROR;
  }

  if (syncStatus === 'error') {
    return getUserSourceErrorMessage(error, 'short') ?? 'Failed to process';
  }

  return error ?? 'Failed to process';
};
